import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { StravaConnectButton } from "@/components/strava-connect-button";
import { useImportProgress } from "@/api/imports";
import { useToast } from "@/hooks/use-toast";
import { SUPPORTED_CITIES, SupportedCity, cityLabel } from "@/lib/cities";
import { ACTIVITY_OPTIONS, toggleActivity } from "@/lib/activity";
import { cn } from "@/lib/utils";
import {
  useBackfillStrava,
  useCreateCityRequest,
  useCreateWorkspace,
  useGetWorkspace,
  useListStravaApps,
} from "@/api/orval";

type Step = "city" | "provisioning" | "strava" | "import";

const StepShell = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <div className="flex min-h-svh items-center justify-center bg-muted p-6">
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <CardTitle className="text-xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  </div>
);

export const Onboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>("city");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<SupportedCity | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [requestCity, setRequestCity] = useState("");
  const [workspaceId, setWorkspaceId] = useState<string>("");
  const [slug, setSlug] = useState<string>("");
  const [activityTypes, setActivityTypes] = useState<string[]>(["run"]);

  const createWorkspace = useCreateWorkspace();
  const backfill = useBackfillStrava();
  const cityRequest = useCreateCityRequest();
  const { startImport: beginImportTracking } = useImportProgress();
  const { data: stravaApps } = useListStravaApps();
  const isStravaConnected = (stravaApps?.length ?? 0) > 0;

  // Poll the workspace while its streets load in the background.
  const { data: workspace } = useGetWorkspace(
    { workspaceId },
    {
      query: {
        enabled: step === "provisioning" && !!workspaceId,
        refetchInterval: 2000,
      },
    },
  );

  useEffect(() => {
    if (step !== "provisioning" || !workspace) return;
    if (workspace.status === "ready") {
      // Skip the connect step if Strava is already linked (e.g. adding a second
      // city); jump straight to importing recent runs into the new city.
      setStep(isStravaConnected ? "import" : "strava");
    } else if (workspace.status === "failed") {
      toast({
        variant: "destructive",
        title: "Couldn't load that city",
        description: "Something went wrong loading the streets. Try again.",
      });
      setStep("city");
    }
  }, [step, workspace, isStravaConnected, toast]);

  const q = query.trim().toLowerCase();
  const suggestions =
    !selected && q.length > 0
      ? SUPPORTED_CITIES.filter((c) =>
          cityLabel(c).toLowerCase().includes(q),
        ).slice(0, 6)
      : [];

  const selectCity = (c: SupportedCity) => {
    setSelected(c);
    setQuery(cityLabel(c));
  };

  const handleAddCity = async () => {
    if (!selected) return;
    try {
      // The server derives a unique slug from the name.
      const created = await createWorkspace.mutateAsync({
        data: {
          name: selected.city,
          place: selected.place,
          activity_types: activityTypes,
        },
      });
      setWorkspaceId(created.id);
      setSlug(created.slug);
      setStep("provisioning");
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Couldn't add that city",
        description: err?.response?.data?.detail ?? "Please try again.",
      });
    }
  };

  const submitRequest = () => {
    const city = requestCity.trim();
    if (!city) return;
    cityRequest.mutate({ data: { text: city } });
    toast({
      title: "Thanks for the suggestion",
      description: `We'll look into adding ${city}.`,
    });
    setRequestCity("");
    setRequesting(false);
  };

  const finish = () => navigate(`/city/${slug}`);

  const startImport = async (window: "week" | "month") => {
    try {
      const res = (await backfill.mutateAsync({ data: { window } })) as {
        import_id?: string;
        token?: string;
      };
      if (res?.import_id && res?.token && workspaceId) {
        // Track progress so the city map shows "importing run X of N" and
        // reveals streets live as they match.
        beginImportTracking({
          importId: res.import_id,
          token: res.token,
          workspaceId,
        });
      }
    } catch {
      // Non-fatal: the import is queued server-side regardless of the stream.
    }
    toast({
      title: "Importing your runs",
      description: "Your map will fill in as we process your recent runs.",
    });
    finish();
  };

  if (step === "city") {
    return (
      <StepShell
        title="Pick your city"
        description="Choose a city to start running every street of."
      >
        <div className="space-y-4">
          <div className="relative">
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              className="mt-1.5"
              placeholder="Start typing, e.g. Barcelona"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelected(null);
              }}
              autoComplete="off"
              autoFocus
            />
            {suggestions.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
                {suggestions.map((c) => (
                  <li key={c.place}>
                    <button
                      type="button"
                      onClick={() => selectCity(c)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                    >
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      {cityLabel(c)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <Label>Activities to track</Label>
            <div className="mt-1.5 flex gap-2">
              {ACTIVITY_OPTIONS.map((a) => {
                const active = activityTypes.includes(a.key);
                return (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() =>
                      setActivityTypes((t) => toggleActivity(t, a.key))
                    }
                    className={cn(
                      "flex-1 rounded-md border px-3 py-2 text-sm transition-colors",
                      active
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-input text-muted-foreground hover:bg-accent",
                    )}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Button
            className="w-full"
            disabled={!selected || createWorkspace.isPending}
            onClick={handleAddCity}
          >
            {createWorkspace.isPending ? "Adding city..." : "Add city"}
          </Button>

          {!requesting ? (
            <button
              type="button"
              onClick={() => setRequesting(true)}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
            >
              Don&apos;t see your city? Request it
            </button>
          ) : (
            <div className="space-y-2 rounded-md border bg-muted/40 p-3">
              <Label htmlFor="request" className="text-xs">
                Which city would you like?
              </Label>
              <div className="flex gap-2">
                <Input
                  id="request"
                  placeholder="City, Country"
                  value={requestCity}
                  onChange={(e) => setRequestCity(e.target.value)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={submitRequest}
                  disabled={!requestCity.trim()}
                >
                  Submit
                </Button>
              </div>
            </div>
          )}
        </div>
      </StepShell>
    );
  }

  if (step === "provisioning") {
    return (
      <StepShell
        title="Setting up your city"
        description="We're loading every street from the map. This takes a moment."
      >
        <div className="flex flex-col items-center gap-3 py-6">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            {selected ? cityLabel(selected) : ""}
          </p>
        </div>
      </StepShell>
    );
  }

  if (step === "strava") {
    return (
      <StepShell
        title="Connect Strava"
        description="Connect Strava so your runs automatically fill in your map."
      >
        <div className="flex flex-col items-center gap-4 py-2">
          <StravaConnectButton onConnected={() => setStep("import")} />
          <Button variant="ghost" onClick={finish}>
            Skip for now
          </Button>
        </div>
      </StepShell>
    );
  }

  return (
    <StepShell
      title="Match your recent runs"
      description={`We'll match runs from your Strava history to ${
        selected ? selected.city : "this city"
      } and fill in your map.`}
    >
      <div className="space-y-3">
        <Button
          className="w-full"
          onClick={() => startImport("month")}
          disabled={backfill.isPending}
        >
          Match my last month
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => startImport("week")}
          disabled={backfill.isPending}
        >
          Match my last week
        </Button>
        <Button variant="ghost" className="w-full" onClick={finish}>
          Skip, I&apos;ll just go for a run
        </Button>
      </div>
    </StepShell>
  );
};
